import { Feature, SpartenType } from '@ps20/bff-models';
import { FileUploader } from '@ps20-frontend/frontend-models';
import {
  UwContext,
  UwContextDescriptor,
  UwEvalContext,
  UwPrintField,
  UwSubmissionField,
} from '../uw-context.model';

const OUTBUILDING_REASON = 'Gesamtfläche der Nebengebäude betragen mehr als 200 qm';

export const OUTBUILDING_TOTAL_AREA_THRESHOLD_SQM = 200;

export interface OutbuildingEvalContext extends UwEvalContext {
  vorvertrag?: { coveredOutbuildingsOver200: boolean };
  outbuildingTotalAreaSqm?: number;
  isInsuredOutbuildingsLeistungDeselected?: boolean;
}

export function isOutbuildingRequired(
  _produkt: unknown,
  ctx: OutbuildingEvalContext,
): boolean {
  if (
    ctx.outbuildingTotalAreaSqm !== undefined &&
    ctx.outbuildingTotalAreaSqm <= OUTBUILDING_TOTAL_AREA_THRESHOLD_SQM
  ) {
    return false;
  }
  if (ctx.isInsuredOutbuildingsLeistungDeselected) {
    return false;
  }
  if (ctx.isNeugeschaeft) {
    return true;
  }
  return ctx.vorvertrag?.coveredOutbuildingsOver200 !== true;
}

export function buildOutbuildingReason(): string {
  return OUTBUILDING_REASON;
}

export interface OutbuildingProdukt {
  nebengebaeudeUeber200?: boolean;
}

export function isOutbuildingRiskBlocking(produkt: OutbuildingProdukt): boolean {
  return produkt.nebengebaeudeUeber200 === true;
}

export const outbuildingDescriptor: UwContextDescriptor<OutbuildingProdukt> = {
  key: UwContext.OUTBUILDING,
  ordinal: 2,
  isBlockingRisk: isOutbuildingRiskBlocking,
  enabledWhen: featureToggles => featureToggles[Feature.UW_NEBENGEBAEUDE] === true,
  appliesTo: spartenType => spartenType === SpartenType.WOHNGEBAEUDE_V2,
  buildReason: buildOutbuildingReason,
  uploader: FileUploader.NEBENGEBAEUDE_FOTOS,
  textFields: [
    {
      id: 'beschreibung',
      labelKey: 'anfragepflichtigeInformationen.nebengebaeudeCard.beschreibung.label',
      required: true,
      maxLength: 500,
      pattern: 'TEXTFIELD_WITH_NEWLINE_BESONDERHEITEN',
    },
    {
      id: 'comment',
      labelKey: 'anfragepflichtigeInformationen.nebengebaeudeCard.comment.label',
      required: false,
      maxLength: 500,
      pattern: 'TEXTFIELD_WITH_NEWLINE_BESONDERHEITEN',
    },
  ],
  i18n: {
    title: 'anfragepflichtigeInformationen.nebengebaeudeCard.addressTitle',
    reason: 'anfragepflichtigeInformationen.underwriterDecision.confirmation.grund.nebengebaeude',
    rejectionMeasure:
      'anfragepflichtigeInformationen.underwriterDecision.rejection.removeNebengebaeude',
  },
  wire: {
    submissionField: UwSubmissionField.OUTBUILDING,
    printField: UwPrintField.OUTBUILDING,
  },
  copy: {
    requiredForTitle: 'anfragepflichtigeInformationen.nebengebaeudeCard.requiredForTitle',
    uploadSectionTitle: 'anfragepflichtigeInformationen.nebengebaeudeCard.fotosUpload.label',
    uploadSectionHint: 'anfragepflichtigeInformationen.nebengebaeudeCard.fotosUpload.hint',
    uploadRequiredErrorKey: 'anfragepflichtigeInformationen.nebengebaeudeCard.fotosUpload.required',
    uploadRequiredErrorDataCy: 'nebengebaeude-file-upload-required-error',
    readonlyTitle: 'anfragepflichtigeInformationen.nebengebaeudeReadonly.title',
    readonlyFieldLabels: {
      beschreibung: 'anfragepflichtigeInformationen.nebengebaeudeReadonly.beschreibungLabel',
      comment: 'anfragepflichtigeInformationen.nebengebaeudeReadonly.commentLabel',
    },
    requiredFieldPlaceholder: {
      beschreibung: 'anfragepflichtigeInformationen.nebengebaeudeCard.beschreibung.placeholder',
    },
    requiredFieldErrorKey: {
      beschreibung: 'anfragepflichtigeInformationen.nebengebaeudeCard.beschreibung.required',
    },
  },
  readonlyCard: { hideWithoutFiles: true },
  fieldDataCy: {
    beschreibung: {
      textarea: 'nebengebaeude-beschreibung',
      errorRequired: 'nebengebaeude-beschreibung-error',
      errorLength: 'nebengebaeude-beschreibung-error-length',
      errorPattern: 'nebengebaeude-beschreibung-error-pattern',
    },
    comment: {
      textarea: 'nebengebaeude-comment',
      errorLength: 'nebengebaeude-comment-error-length',
      errorPattern: 'nebengebaeude-comment-error-pattern',
    },
  },
};
